import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button,
    Avatar
} from '@mui/material';

import {
    CheckCircleOutline,
    HomeRepairService,
    AccountBalanceWallet,
    PeopleAlt,
    ExitToApp,
    ArrowForwardIos,
    ErrorOutline,
    InfoOutlined,
    DoneAll,
    LocationOn
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    { id: 'introduction', label: 'はじめに：ルームシェア退去の基礎知識' },
    { id: 'flow', label: '退去手続きの流れと準備' },
    { id: 'genjokaihuku', label: '原状回復義務のポイント' },
    { id: 'shikikin', label: '敷金返還でトラブルを防ぐコツ' },
    { id: 'joint-contract', label: '共同名義の解約と注意点' },
    { id: 'moving', label: '次の住居へのスムーズな移行' },
    { id: 'faq', label: 'よくあるQ&A・まとめ' }
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 1, sm: 3 } }}>
            {/* 目次 */}
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem 
                                    key={item.id} 
                                    sx={{ p: 0.5, display: 'flex', alignItems: 'center', mb: 0.5 }}
                                >
                                    <Box
                                        sx={{
                                            minWidth: 32,
                                            minHeight: 32,
                                            bgcolor: '#f5f5f5',
                                            color: '#888',
                                            fontWeight: 'bold',
                                            borderRadius: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mr: 1,
                                            fontSize: '1.1rem'
                                        }}
                                    >
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.95rem', textAlign: 'left' }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    はじめに：ルームシェア退去の基礎知識
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアを快適に楽しんだ後、いざ「退去」となると、何から手をつけてよいのか戸惑う方も少なくありません。
                    <Chip label="原状回復" color="primary" variant="outlined" size="small" sx={{ ml: 1 }}/>
                    <Chip label="敷金返還" color="primary" variant="outlined" size="small" sx={{ ml: 1 }}/>
                    <Chip label="共同名義" color="primary" variant="outlined" size="small" sx={{ ml: 1 }}/>
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    退去時には、手続きや原状回復、敷金返還、名義の解約など、通常の一人暮らしとは異なるルームシェア特有の注意点が盛りだくさん。<br/>
                    この記事では、ルームシェア退去の流れを徹底解説し、トラブルなく次のステップへ進むための実践的なポイントを紹介します。
                </Typography>
                <Box sx={{ my: 3 }}>
                    <img
                        src="/images/blog/share-house-leaving_roomshare-taikyo-genjokaihuku-shikikin.jpg"
                        alt="明るい共有リビングでルームメイトたちが退去の話し合いをしている様子。笑顔や真剣な表情が交じり、和やかな雰囲気の中で円満な話し合いが行われていることを表す。室内はシンプルでモダンな家具が配置され、自然光が差し込んでいる。"
                        style={{
                            width: '100%',
                            borderRadius: 12,
                            marginBottom: 8,
                            objectFit: 'cover'
                        }}
                    />
                    <Typography variant="caption" display="block" align="center" sx={{ color: 'text.secondary' }}>
                        ルームシェア退去時には、住人同士の円滑なコミュニケーションが大切です
                    </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
            </Box>

            {/* 退去手続きの流れと準備 */}
            <Box id="flow" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    退去手続きの流れと準備
                </Typography>
                <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: '#fafafa' }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        <HomeRepairService sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                        退去の基本ステップ
                    </Typography>
                    <List>
                        <ListItem>
                            <ListItemIcon><ArrowForwardIos color="primary" fontSize="small" /></ListItemIcon>
                            <ListItemText
                                primary={<Typography variant="body2" fontWeight="bold">管理会社・大家へ退去連絡</Typography>}
                                secondary="通常1〜2ヶ月前に書面で連絡。共通のルールを事前に確認しましょう。"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><ArrowForwardIos color="primary" fontSize="small" /></ListItemIcon>
                            <ListItemText
                                primary={<Typography variant="body2" fontWeight="bold">ルームメイト間の合意形成</Typography>}
                                secondary="退去日や原状回復分担についてルームメイトと明確に話し合うことが大切。"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><ArrowForwardIos color="primary" fontSize="small" /></ListItemIcon>
                            <ListItemText
                                primary={<Typography variant="body2" fontWeight="bold">退去立会いの日程調整</Typography>}
                                secondary="管理会社または大家と現地で室内確認を行う日を調整。"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><ArrowForwardIos color="primary" fontSize="small" /></ListItemIcon>
                            <ListItemText
                                primary={<Typography variant="body2" fontWeight="bold">公共料金・ネット等の精算</Typography>}
                                secondary="ガス・電気・水道・インターネットの名義・精算も忘れずに。"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><ArrowForwardIos color="primary" fontSize="small" /></ListItemIcon>
                            <ListItemText
                                primary={<Typography variant="body2" fontWeight="bold">鍵の返却と最終チェック</Typography>}
                                secondary="全員分の鍵、共用部の清掃状況などを最終確認。"
                            />
                        </ListItem>
                    </List>
                </Paper>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Box sx={{ mb: 2 }}>
                            
                            <Typography variant="caption" display="block" align="center" sx={{ color: 'text.secondary' }}>
                                退去前の共同清掃は、原状回復や敷金返還にも影響します
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: '#f0f8ff', borderRadius: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                                退去準備のチェックポイント
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                    <ListItemText primary="住人全員に退去意向を共有し、役割分担を決める" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                    <ListItemText primary="私物と共用物を明確に分けて整理" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                    <ListItemText primary="退去立会い日までに掃除・修繕を完了" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                                    <ListItemText primary="トラブル防止のため、写真で現状を記録" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
                <Divider sx={{ my: 3 }} />
            </Box>

            {/* 原状回復義務のポイント */}
            <Box id="genjokaihuku" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    原状回復義務のポイント
                </Typography>
                <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: '#f6fafe' }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        <HomeRepairService sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                        原状回復とは？
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        原状回復義務とは、入居時の状態に戻す責任を指します（国交省ガイドラインに準拠）。
                        ただし、「経年劣化」や「通常使用による傷み」は借主の負担になりません。
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon><InfoOutlined color="info" /></ListItemIcon>
                            <ListItemText primary="壁紙の小さな変色や日焼けは「経年劣化」と判断される場合が多い" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><ErrorOutline color="error" /></ListItemIcon>
                            <ListItemText primary="タバコのヤニやペットによる傷・臭いは借主負担になることが多い" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><InfoOutlined color="info" /></ListItemIcon>
                            <ListItemText primary="共用スペースの破損は、全員で責任を分担することが一般的" />
                        </ListItem>
                    </List>
                </Paper>
                <Box sx={{ my: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                        原状回復のトラブルを防ぐポイント
                    </Typography>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={6}>
                            <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2, mb: 2 }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>✔ 退去前に室内・共用部の現状を写真や動画で記録</Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>✔ 修繕が必要な箇所は、できる範囲で対応しておく</Typography>
                                <Typography variant="body2">✔ 立会い時に修繕費用の説明をしっかり受ける</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Paper elevation={0} sx={{ p: 2, bgcolor: '#fffbe7', borderRadius: 2 }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>✔ 費用分担はルームメイトと事前に合意し書面化</Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>✔ 管理会社とのやりとりは必ず記録に残す</Typography>
                                <Typography variant="body2">✔ ガイドラインの確認や相談窓口も活用</Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>
                <Divider sx={{ my: 3 }} />
            </Box>

            {/* 敷金返還でトラブルを防ぐコツ */}
            <Box id="shikikin" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    敷金返還でトラブルを防ぐコツ
                </Typography>
                <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: '#f0f7fa' }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        <AccountBalanceWallet sx={{ mr: 1, color: 'primary.main', verticalAlign: 'middle' }} />
                        敷金返還の基本
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        敷金は、退去時に部屋の修繕費用を差し引いて返還されるお金です。
                        ルームシェアの場合、<b>「誰に、いくら、どのタイミングで返還されるか」</b>は契約形態によって大きく異なります。
                    </Typography>
                </Paper>
                <Box sx={{ my: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Paper elevation={0} sx={{ p: 2, bgcolor: '#fff', borderRadius: 2, mb: 2 }}>
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                                    敷金返還トラブル事例
                                </Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon><ErrorOutline color="error" /></ListItemIcon>
                                        <ListItemText primary="共同名義で入居、全員退去しないと返金されない" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><ErrorOutline color="error" /></ListItemIcon>
                                        <ListItemText primary="代表名義人だけに返金され、他の住人とトラブル" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><ErrorOutline color="error" /></ListItemIcon>
                                        <ListItemText primary="修繕費の分担が曖昧で揉める" />
                                    </ListItem>
                                </List>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper elevation={0} sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                                    トラブル防止のポイント
                                </Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon><DoneAll color="success" /></ListItemIcon>
                                        <ListItemText primary="契約書の敷金返還条項を事前に確認" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><DoneAll color="success" /></ListItemIcon>
                                        <ListItemText primary="分担割合や精算ルールを明文化" />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon><DoneAll color="success" /></ListItemIcon>
                                        <ListItemText primary="代表名義人が返金を一括受領する場合、住人間で清算書を作成" />
                                    </ListItem>
                                </List>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>
                <Box sx={{ my: 3 }}>
                    <img
                        src="/images/blog/deposit-refund_roomshare-taikyo-genjokaihuku-shikikin.jpg"
                        alt="オフィスデスク上で敷金返還に関する書類に複数人がサインしている様子。真剣な表情の男女がペンを持ち、契約書類や電卓、スマートフォンが並ぶ。トラブル防止のための書面化の重要性を示す写真。"
                        style={{
                            width: '100%',
                            borderRadius: 10,
                            objectFit: 'cover',
                            marginBottom: 6
                        }}
                    />
                    <Typography variant="caption" display="block" align="center" sx={{ color: 'text.secondary' }}>
                        敷金返還のトラブル防止には、契約内容の確認と住人間の書面合意が有効です
                    </Typography>
                </Box>
                <Divider sx={{ my: 3 }} />
            </Box>

            {/* 共同名義の解約と注意点 */}
            <Box id="joint-contract" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    共同名義の解約と注意点
                </Typography>
                <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: '#f8f9fa' }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        <PeopleAlt sx={{ mr: 1, color: 'primary.main', verticalAlign: 'middle' }} />
                        共同名義・代表名義の違い
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        ルームシェア契約には、<b>共同名義（全員連帯責任）</b>と<b>代表名義（1人が契約、他は同居人）</b>があります。
                        退去や解約の際は名義の種類によって手続きや責任範囲が異なります。
                    </Typography>
                </Paper>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2, mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                                共同名義の注意点
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><InfoOutlined color="info" /></ListItemIcon>
                                    <ListItemText primary="全員同時退去が原則。1人だけ抜ける場合は再契約が必要な場合も" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><InfoOutlined color="info" /></ListItemIcon>
                                    <ListItemText primary="解約通知も全員の署名・同意が必要なことが多い" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><ErrorOutline color="error" /></ListItemIcon>
                                    <ListItemText primary="敷金返還も全員で分配。代表者のみ受領は後トラブルの元" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: '#fffbe7', borderRadius: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                                代表名義の注意点
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><InfoOutlined color="info" /></ListItemIcon>
                                    <ListItemText primary="代表名義人が退去時の責任を負う" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><ErrorOutline color="error" /></ListItemIcon>
                                    <ListItemText primary="他の住人が退去しても契約継続の場合は名義人の変更不可が多い" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><ErrorOutline color="error" /></ListItemIcon>
                                    <ListItemText primary="敷金は原則代表名義人にのみ返還。内部で清算書を作成しましょう" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
                <Divider sx={{ my: 3 }} />
            </Box>

            {/* 次の住居へのスムーズな移行 */}
            <Box id="moving" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    次の住居へのスムーズな移行
                </Typography>
                <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: '#f2f9f8' }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        <ExitToApp sx={{ mr: 1, color: 'primary.main', verticalAlign: 'middle' }} />
                        新生活へのステップ
                    </Typography>
                    <List>
                        <ListItem>
                            <ListItemIcon><LocationOn color="primary" /></ListItemIcon>
                            <ListItemText primary="次の住居の契約日・入居日と、現住居の退去日をしっかり調整" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                            <ListItemText primary="引っ越し業者の手配や荷造りは早めに" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                            <ListItemText primary="転居届や郵便転送、役所手続きも忘れずに" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleOutline color="success" /></ListItemIcon>
                            <ListItemText primary="ルームメイトへの挨拶や感謝の気持ちを言葉に" />
                        </ListItem>
                    </List>
                </Paper>
                <Box sx={{ my: 2 }}>
                    <img
                        src="/images/blog/moving-box_roomshare-taikyo-genjokaihuku-shikikin.jpg"
                        alt="ダンボール箱に荷物を詰める若い男女のルームメイトたち。明るい室内で引越し作業を協力して進めており、笑顔や少し名残惜しそうな表情が見える。新生活への前向きな気持ちと、今までの感謝が伝わる雰囲気。"
                        style={{
                            width: '100%',
                            borderRadius: 10,
                            objectFit: 'cover',
                            marginBottom: 6
                        }}
                    />
                    <Typography variant="caption" display="block" align="center" sx={{ color: 'text.secondary' }}>
                        引越し準備は、ルームメイトとの思い出を振り返る良い機会にもなります
                    </Typography>
                </Box>
                <Divider sx={{ my: 3 }} />
            </Box>

            {/* FAQ・まとめ */}
            <Box id="faq" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    よくあるQ&A・まとめ
                </Typography>
                <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: '#f6fafe' }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        <InfoOutlined sx={{ mr: 1, color: 'primary.main', verticalAlign: 'middle' }} />
                        退去時のよくある質問
                    </Typography>
                    <List>
                        <ListItem>
                            <ListItemIcon><ErrorOutline color="error" /></ListItemIcon>
                            <ListItemText
                                primary="Q. ルームメイトが残る場合、部分退去は可能？"
                                secondary="A. 共同名義なら原則不可。管理会社の許可と再契約が必要なことが多いです。"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><ErrorOutline color="error" /></ListItemIcon>
                            <ListItemText
                                primary="Q. 原状回復費用をめぐってトラブルになったら？"
                                secondary="A. 写真記録や会話履歴をもとに管理会社と協議し、解決しない場合は消費者センター等に相談を。"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><ErrorOutline color="error" /></ListItemIcon>
                            <ListItemText
                                primary="Q. 敷金返還が遅い場合はどうすれば？"
                                secondary="A. 契約書の返還期限を確認し、書面で督促。法的措置も視野に入れて冷静に対応しましょう。"
                            />
                        </ListItem>
                    </List>
                </Paper>
                <Box sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                        まとめ：円満な退去で新しい一歩を
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        ルームシェアの退去は、単なる引越し以上に配慮が必要です。
                        住人同士の信頼関係や、管理会社との円滑なやりとりが重要な鍵。
                        一つひとつの手続きを丁寧に進めることで、気持ちよく新生活をスタートできます。
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        不明点がある場合は、必ず契約書やガイドラインを再確認し、適切な相談窓口を利用しましょう。
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-taikyo-genjokaihuku-shikikin',
    slug: 'roomshare-taikyo-genjokaihuku-shikikin',
    title: 'ルームシェアで退去する際の注意点：原状回復と敷金返還のポイント',
    excerpt: '退去手続きの流れ\n原状回復義務\n敷金返還トラブル防止\n共同名義の解約\n次の住居への移行',
    coverImage: '/images/blog/cover-roomshare-leaving_roomshare-taikyo-genjokaihuku-shikikin.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。住人同士の信頼関係や円滑なコミュニケーションが、退去時のトラブル防止や円満な移行に繋がる雰囲気を表現した写真。',
    publishedAt: '2024-06-12T00:00:00.000Z',
    isPublished: true,
    categories: ['ルームシェア', '退去・引越し', '賃貸契約'],
    tags: ['原状回復', '敷金返還', '共同名義', '退去トラブル', 'ルームシェア退去'],
    author: {
        name: '佐藤 洋介',
        avatar: '/images/avatars/yosuke-sato.jpg',
        bio: '不動産業界歴15年。賃貸・ルームシェア専門のアドバイザー。住まいのトラブル予防と円満な住替えサポートが得意。',
        profilePicture: '/images/avatars/yosuke-sato.jpg'
    },
    viewCount: 1520,
    contentComponent: PostContent
};

export default post;